'use client'

import { inputBase, inputOutlined } from '@/core/constants/class-names'
import { useResize } from '@/core/hooks/use-resize'
import { cn } from '@/core/lib/utils/cn'
import { debounce } from '@/core/lib/utils/debounce'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/core/ui/atoms/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/core/ui/atoms/popover'
import { useDisclosure } from '@heroui/react'
import { Check, ChevronDown, X } from 'lucide-react'
import { useState } from 'react'
import { Spinner } from '../atoms/spinner'
import { createInput } from './input-factory'

type ComboboxItem = {
  value: string
  label: string

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

type ComboboxProps<T extends ComboboxItem> = {
  items: T[]
  isLoading?: boolean
  isLoadingNext?: boolean
  disabled?: boolean
  value?: string
  onChange?: (value: string | null, item?: T) => void
  placeholder?: React.ReactNode
  searchPlaceholder?: string
  emptyMessage?: string
  startAction?: (params: { onClick: () => void }) => React.ReactNode
  onSearch?: (query: string) => void
  onScrollEnd?: () => void
  onClear?: () => void

  renderTrigger?: (params: {
    selected?: T
    onClick: () => void
    disabled?: boolean
  }) => React.ReactNode
  renderItem?: (item: T, selected: boolean) => React.ReactNode
  renderEmpty?: () => React.ReactNode
  resetSearch?: () => void

  className?: string
  popoverClassName?: string

  disableDefaultStyles?: boolean

  error?: string | boolean
  errorMessage?: string

  popoverStyle?: React.CSSProperties

  filter?: (value: string, search: string, keywords?: string[]) => number
}

export function BaseCombobox<T extends ComboboxItem>({
  items = [],
  value: controlledValue,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  renderTrigger,
  renderItem,
  renderEmpty,
  className,
  popoverClassName,
  onSearch,
  onScrollEnd,
  isLoading,
  isLoadingNext,
  disabled,
  disableDefaultStyles,
  error,
  errorMessage,
  onClear,
  startAction,
  popoverStyle,
  resetSearch,
  filter,
}: ComboboxProps<T>) {
  const { isOpen, onClose, onOpenChange } = useDisclosure()

  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
    null,
  )
  const { ref, size } = useResize()

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : uncontrolledValue
  const selected = items.find(i => i.value === value)

  const handleSelect = (val: string) => {
    const item = items.find(i => i.value === val)
    if (!isControlled) {
      setUncontrolledValue(val)
    }
    onChange?.(val, item)
    onClose()
    resetSearch?.()
  }

  const handleClear = () => {
    onClear?.()
    onChange?.(null)
    setUncontrolledValue(null)
  }

  const handleScroll = debounce(() => onScrollEnd?.(), 200)

  const hasError = Boolean(error)

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild ref={ref} className='cursor-pointer'>
        {renderTrigger ? (
          renderTrigger({
            selected,
            onClick: onOpenChange,
            disabled: isLoading || disabled,
          })
        ) : (
          <button
            role='combobox'
            aria-controls='combobox-list'
            aria-expanded={isOpen}
            aria-haspopup='listbox'
            aria-label='toggle open'
            aria-disabled={isLoading || disabled}
            onClick={onOpenChange}
            className={
              disableDefaultStyles
                ? cn(
                    'flex h-11 w-full items-center justify-between px-2',
                    className,
                    hasError && 'ring-red text-red',
                  )
                : cn(
                    'flex w-full items-center justify-between px-2',
                    'transition-shadow duration-200 ease-in-out',
                    isOpen && 'ring-navy ring-2 ring-offset-0 outline-none',
                    inputBase,
                    inputOutlined,
                    className,
                    hasError && 'ring-red text-red',
                  )
            }
            disabled={isLoading || disabled}
          >
            {selected ? selected.label : placeholder}
            <div className='flex items-center'>
              {selected ? (
                <span
                  role='button'
                  className='cursor-pointer rounded-full p-1 transition-colors hover:bg-gray-200'
                  onKeyDown={() => {}}
                  tabIndex={0}
                  onClick={e => {
                    e.stopPropagation()
                    handleClear()
                  }}
                >
                  <X className='h-4 w-4 shrink-0 opacity-50' />
                </span>
              ) : null}
              {isLoading ? (
                <Spinner color='darkGrey' />
              ) : (
                <ChevronDown
                  className={cn(
                    'ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform duration-100',
                    isOpen && 'rotate-180',
                  )}
                />
              )}
            </div>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        avoidCollisions={false}
        className={cn(
          'w-full rounded-md border-none p-0 shadow-sm ring-1 shadow-current',
          popoverClassName,
        )}
        style={popoverStyle ? popoverStyle : { width: size.width }}
        align='start'
      >
        <Command shouldFilter={Boolean(filter)} filter={filter}>
          <CommandInput
            onValueChange={onSearch}
            placeholder={searchPlaceholder}
          />
          <CommandList onScrollEnd={handleScroll} className=''>
            <CommandEmpty>
              {renderEmpty ? renderEmpty() : emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {startAction ? (
                <CommandItem className='p-0' onClick={() => alert('hi')}>
                  {startAction?.({ onClick: onClose })}
                </CommandItem>
              ) : null}
              {items.map((item, index) => {
                // cmdk computes an item's value by iterating [value, children,
                // ref]; because `typeof null === 'object'` it runs
                // `'current' in value` and throws when `value` is null. Coalesce
                // to a safe string so a null/undefined value can never crash it.
                const itemValue = item.value == null ? '' : String(item.value)
                const selected = value === item.value
                return (
                  <CommandItem
                    key={itemValue || `item-${index}`}
                    value={itemValue}
                    keywords={[item.label]}
                    onSelect={() => handleSelect(itemValue)}
                    className='cursor-pointer rounded-sm transition-colors duration-150 hover:bg-gray-100'
                  >
                    {renderItem ? (
                      renderItem(item, selected)
                    ) : (
                      <>
                        {item.label}
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            selected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </>
                    )}
                  </CommandItem>
                )
              })}
              {isLoadingNext && (
                <div className='flex justify-center py-2'>
                  <Spinner color='darkGrey' />
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
      {hasError && errorMessage && (
        <p className='text-red text-sm'>{errorMessage}</p>
      )}
    </Popover>
  )
}

export const Combobox = createInput(BaseCombobox, ['label'])
export const FormCombobox = createInput(BaseCombobox, ['form', 'label'])
